class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initializeSwipeGesture();
    
    // Debug: Track constructor calls
    if (!window.cartDrawerInstances) window.cartDrawerInstances = 0;
    window.cartDrawerInstances++;

    // Create ICR event handler once to prevent duplicate listeners (Bug Fix)
    this.handleICRClick = (event) => {
      // Ensure this handler only responds to events from within this component
      if (!event.target.closest('cart-drawer')) {
        return;
      }

      const clickId = Math.random().toString(36).substr(2, 9);
      
      // Check if target or closest parent has the data attribute
      const learnMoreBtn = event.target.closest('[data-icr-learn-more]');
      const addCartBtn = event.target.closest('[data-icr-add-cart]');
      
      if (learnMoreBtn) {

        event.preventDefault();
        event.stopPropagation();
        this.handleICRLearnMore(event);
      } else if (addCartBtn) {

        event.preventDefault();
        event.stopPropagation();
        this.handleICRAddCart(event);
      } else {

      }
    };
    
    // Give this instance a unique ID for debugging
    this.instanceId = window.cartDrawerInstances;
  }

  connectedCallback() {
    // Debug: Check for duplicate cart-drawer elements
    const allCartDrawers = document.querySelectorAll('cart-drawer');

    if (allCartDrawers.length > 1) {
      console.warn(`⚠️ WARNING: Multiple cart-drawer elements detected! This could cause duplicate events.`);
      allCartDrawers.forEach((drawer, index) => {
        console.log(`  - cart-drawer #${index + 1}:`, drawer);
      });
    }
    
    // TEMPORARILY DISABLED: Cross-sync between cart page and drawer
    // This prevents content mixing issues while maintaining individual functionality
    // TODO: Re-enable once section isolation is confirmed working
    /*
    this.cartUpdateUnsubscriber = subscribe('cart-update', (event) => {
      if (event.source === 'cart-drawer') {
        return; // Don't update if the change came from the drawer itself
      }
      
      // SAFETY: Only update drawer when it's actually open and visible
      if (this.classList.contains('active') && !document.hidden) {
        this.updateDrawerFromCartChanges();
      }
      // If drawer is closed, it will get fresh content next time it opens
    });
    */
    
    // Listen for ICR adapter additions from PDP
    document.addEventListener('icrAdapterAdded', (event) => {

      setTimeout(() => {
        this.hideICRUpsellsForCompletedItems();
      }, 500); // Small delay to ensure cart is updated
    });
    
    // Initialize ICR upsell when element is connected to DOM

    this.initializeICRUpsell();
    
    // Also check for existing ICR adapters in cart and hide appropriate upsells
    setTimeout(() => {
      this.hideICRUpsellsForCompletedItems();
      this.reorderICRAdapters(); // Reorder items on initial load
    }, 100);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  updateDrawerFromCartChanges() {
    // CRITICAL: Only update if drawer is currently open to prevent content mixing
    if (!this.classList.contains('active')) {
      return; // Don't update closed drawer to prevent section mixing
    }
    
    // Fetch multiple sections specifically for drawer updates
    const sectionsToFetch = ['cart-drawer', 'cart-icon-bubble'];
    const sectionParams = sectionsToFetch.map(section => `sections=${section}`).join('&');
    
    fetch(`${window.Shopify.routes.root}cart?${sectionParams}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        
        // VALIDATION: Ensure we got drawer content, not main cart content
        const drawerSection = html.querySelector('[id*="shopify-section-cart-drawer"]') || 
                             html.querySelector('.cart-drawer') ||
                             html.querySelector('#CartDrawer');
        
        if (!drawerSection) {
          console.warn('Cart Drawer: Invalid section response - missing drawer content');
          return; // Abort if we don't have proper drawer content
        }
        
        // VALIDATION: Check for main cart indicators that shouldn't be in drawer
        const hasMainCartIndicators = html.querySelector('.cart__warnings') || 
                                     html.querySelector('.cart__empty-text') ||
                                     html.querySelector('cart-items') ||
                                     html.querySelector('thead');
        
        if (hasMainCartIndicators) {
          console.warn('Cart Drawer: Detected main cart content in drawer response - aborting update');
          return; // Abort if main cart content detected
        }
        
        // Update cart items section (drawer-specific)
        const sourceItems = html.querySelector('#CartDrawer-CartItems');
        const targetItems = this.querySelector('#CartDrawer-CartItems');
        if (sourceItems && targetItems && sourceItems.innerHTML.includes('cart-item')) {
          targetItems.innerHTML = sourceItems.innerHTML;
        }
        
        // Update cart footer/totals section
        const sourceFooter = html.querySelector('.cart-drawer__footer');
        const targetFooter = this.querySelector('.cart-drawer__footer');
        if (sourceFooter && targetFooter) {
          targetFooter.innerHTML = sourceFooter.innerHTML;
        }
        
        // Update cart icon bubble separately if needed
        if (sectionsToFetch.includes('cart-icon-bubble')) {
          const sourceIcon = html.querySelector('#cart-icon-bubble');
          const targetIcon = document.querySelector('#cart-icon-bubble');
          if (sourceIcon && targetIcon) {
            targetIcon.innerHTML = sourceIcon.innerHTML;
          }
        }
        
        // Re-initialize ICR upsell after content update
        this.initializeICRUpsell();
      })
      .catch((e) => {
        console.warn('Cart Drawer: Update failed, will retry on next open');
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  initializeSwipeGesture() {
    // Only enable on mobile devices
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    
    const drawer = this.querySelector('.drawer__inner');
    if (!drawer) return;
    
    let startX, startY, startTime, isValidSwipe = false;
    let isDragging = false;
    
    const resetDrawerPosition = () => {
      drawer.style.transform = '';
      drawer.style.transition = '';
      isDragging = false;
    };
    
    drawer.addEventListener('touchstart', (e) => {
      // Don't interfere if touching interactive elements
      const target = e.target.closest('button, input, textarea, select, a, [role="button"]');
      if (target) return;
      
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      startTime = Date.now();
      
      // Calculate touch position relative to the drawer element
      const drawerRect = drawer.getBoundingClientRect();
      const touchRelativeToDrawer = touch.clientX - drawerRect.left;
      
      // Extended touch zone: from -50px to 50px relative to drawer's left edge
      // This allows touches even before the drawer starts (to the left of it)
      isValidSwipe = touchRelativeToDrawer >= -50 && touchRelativeToDrawer <= 50;
      
      if (isValidSwipe) {
        // Disable CSS transition during drag
        drawer.style.transition = 'none';
        // Prevent browser swipe-back gesture
        e.preventDefault();
      }
    });
    
    drawer.addEventListener('touchmove', (e) => {
      if (!isValidSwipe) return;
      
      const touch = e.touches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = Math.abs(touch.clientY - startY);
      
      // If we haven't started dragging yet, check if this is a horizontal gesture
      if (!isDragging) {
        // Require minimum horizontal movement to start
        if (Math.abs(deltaX) < 10) return;
        
        // Ignore if more vertical than horizontal movement
        if (deltaY > Math.abs(deltaX)) {
          isValidSwipe = false;
          return;
        }
        
        isDragging = true;
      }
      
      // Only allow rightward swipes (positive deltaX)
      if (deltaX > 0 && isDragging) {
        // Limit maximum drag distance to prevent overextension
        const maxDrag = 250;
        const dragAmount = Math.min(deltaX, maxDrag);
        
        drawer.style.transform = `translateX(${dragAmount}px)`;
        
        // Add resistance effect as user approaches max drag
        if (deltaX > maxDrag * 0.8) {
          drawer.style.transform = `translateX(${maxDrag * 0.8 + (deltaX - maxDrag * 0.8) * 0.3}px)`;
        }
        
        // Prevent browser gestures and scrolling while dragging
        e.preventDefault();
        e.stopPropagation();
      }
    });
    
    drawer.addEventListener('touchend', (e) => {
      if (!isValidSwipe || !isDragging) {
        resetDrawerPosition();
        return;
      }
      
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - startX;
      const deltaTime = Date.now() - startTime;
      const velocity = deltaX / deltaTime; // pixels per millisecond
      
      // Re-enable transitions
      drawer.style.transition = '';
      
      // Determine if gesture should close the drawer
      const shouldClose = deltaX > 120 || (deltaX > 60 && velocity > 0.3);
      
      if (shouldClose) {
        this.close();
      } else {
        // Snap back to original position
        drawer.style.transform = 'translateX(0)';
      }
      
      // Reset state
      isValidSwipe = false;
      isDragging = false;
    });
    
    // Handle touch cancel (e.g., if user receives a call during swipe)
    drawer.addEventListener('touchcancel', () => {
      if (isValidSwipe || isDragging) {
        resetDrawerPosition();
        isValidSwipe = false;
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // Ensure drawer starts in correct position (reset any lingering swipe transforms)
    const drawer = this.querySelector('.drawer__inner');
    if (drawer) {
      drawer.style.transform = '';
      drawer.style.transition = '';
    }
    
    // VALIDATION: Check drawer content is valid before fully opening
    const drawerItems = this.querySelector('#CartDrawer-CartItems');
    const hasMainCartIndicators = this.querySelector('.cart__warnings') || 
                                 this.querySelector('.cart__empty-text') ||
                                 this.querySelector('cart-items') ||
                                 this.querySelector('thead');
    
    if (hasMainCartIndicators && drawerItems) {
      console.warn('Cart Drawer: Detected invalid content, refreshing...');
      // Force refresh drawer content if it contains main cart elements
      fetch(`${window.Shopify.routes.root}cart?sections=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const freshDrawerItems = html.querySelector('#CartDrawer-CartItems');
          if (freshDrawerItems && !responseText.includes('thead')) {
            drawerItems.innerHTML = freshDrawerItems.innerHTML;
            // Re-initialize ICR after content refresh
            this.initializeICRUpsell();
          }
        })
        .catch(() => {
          // Silently handle - drawer will work with current content
        });
    }
    
    // Initialize ICR upsell when drawer opens
    this.initializeICRUpsell();
    
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    
    // Reset any transform styles that may have been applied during swipe gesture
    const drawer = this.querySelector('.drawer__inner');
    if (drawer) {
      drawer.style.transform = '';
      drawer.style.transition = '';
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    
    // Validate that we have the correct sections
    const expectedSections = this.getSectionsToRender().map(s => s.id);
    const availableSections = Object.keys(parsedState.sections || {});
    const hasCorrectSections = expectedSections.every(section => availableSections.includes(section));
    
    if (!hasCorrectSections) {
      // If we don't have cart-drawer section but have main-cart-items, this is wrong
      if (!availableSections.includes('cart-drawer') && availableSections.includes('main-cart-items')) {
        return; // Don't render wrong sections
      }
    }
    
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    
    // Use normal section rendering flow for all sections including cart-icon-bubble
    const sectionsToRender = this.getSectionsToRender();
    
    // Use smart section manager for optimized rendering
    if (window.cartSectionManager) {
      window.cartSectionManager.smartRender(this, parsedState, sectionsToRender);
    } else {
      this.batchUpdateSections(parsedState, sectionsToRender);
    }

    // Use requestAnimationFrame for better performance instead of setTimeout
    // This ensures DOM updates are complete before proceeding
    requestAnimationFrame(async () => {
      
             // Check if content was actually updated
       const cartItemsContainer = this.querySelector('#CartDrawer-CartItems');
       const actualCartItems = cartItemsContainer ? cartItemsContainer.querySelectorAll('.cart-drawer-item') : [];
       const hasCorrectContent = cartItemsContainer && !cartItemsContainer.innerHTML.includes('main-cart-items');
       
       if (!hasCorrectContent) {
         // Don't open if content is wrong
         return;
       }
      
      // CRITICAL FIX: Add small delay to ensure DOM updates are fully complete
      // This prevents race conditions where drawer opens before content is fully rendered
      await new Promise(resolve => setTimeout(resolve, 50));
      
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // Re-initialize swipe gesture after content is updated since DOM elements were replaced
      this.initializeSwipeGesture();
      // Initialize ICR upsell functionality
      this.initializeICRUpsell();
      
      this.open();

    });
  }

  /**
   * Batch update all sections to minimize DOM reflows
   */
  batchUpdateSections(parsedState, sectionsToRender = null) {
    // Create a single DOMParser instance for reuse
    const parser = new DOMParser();
    
    // Prepare all updates before applying them
    const updates = [];
    
    const sections = sectionsToRender || this.getSectionsToRender();
    sections.forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      
      if (sectionElement && parsedState.sections[section.id]) {
        const newHTML = this.getSectionInnerHTMLOptimized(
          parsedState.sections[section.id], 
          section.selector, 
          parser
        );
        updates.push({ element: sectionElement, html: newHTML });
      }
    });
    
    // Apply all updates in a single operation using DocumentFragment for better performance
    updates.forEach(update => {
      update.element.innerHTML = update.html;
    });
    
    // Re-initialize ICR upsell after DOM updates
    this.initializeICRUpsell();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  /**
   * Optimized version that reuses DOMParser instance
   */
  getSectionInnerHTMLOptimized(html, selector = '.shopify-section', parser) {
    return parser.parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  initializeICRUpsell() {
    // Check if ICR upsell is enabled
    if (!window.themeSettings?.icr_upsell_enabled) return;
    
    // Check if ICR adapter product is available/in stock
    if (!window.themeSettings?.icr_adapter_available) return;
    
    // Find all line-item ICR containers
    const icrContainers = this.querySelectorAll('.line-item-icr-upsell');
    if (!icrContainers.length) {
      return;
    }

    // Remove existing listeners from ALL containers first (Bug Fix)
    const allContainers = this.querySelectorAll('.line-item-icr-upsell');
    allContainers.forEach((container) => {
      container.removeEventListener('click', this.handleICRClick);
    });
    
    // Add event listener to new containers using the pre-created handler
    icrContainers.forEach((container) => {
      container.addEventListener('click', this.handleICRClick);
    });
  }

  handleICRLearnMore(event) {
    // Get ICR adapter product handle for navigation
    const icrProductHandle = window.themeSettings?.icr_adapter_product_handle;
    
    if (!icrProductHandle) {
      console.error('ICR adapter product not configured');
      return;
    }

    // Navigate directly to the ICR product page
    window.location.href = `/products/${icrProductHandle}`;
  }

  async handleICRAddCart(event) {
    // Generate unique ID for this specific call
    const callId = Math.random().toString(36).substr(2, 9);
    
    // Check if this method is already running to prevent double execution
    if (this._icrAddInProgress) {
      console.warn(`⚠️ WARNING: ICR add already in progress, ignoring duplicate call`);
      return;
    }
    
    this._icrAddInProgress = true;
    
    const addCartBtn = event.target.closest('[data-icr-add-cart]');
    const icrContainer = event.target.closest('.line-item-icr-upsell');
    
    if (!addCartBtn || !icrContainer) {
      this._icrAddInProgress = false;
      return;
    }

    // Get line item context
    const lineItemKey = icrContainer.dataset.lineItemKey;
    const productId = icrContainer.dataset.productId;

    // Apply optimistic UI - hide upsell immediately for instant feedback
    this.hideICRUpsellOptimistically(icrContainer, addCartBtn);

    // Show loading state
    this.setICRButtonLoading(addCartBtn, true);

    try {
      // Get ICR adapter product handle
      const icrProductHandle = window.themeSettings?.icr_adapter_product_handle;
      
      if (!icrProductHandle) {
        throw new Error('ICR adapter product not configured');
      }

      // Fetch the ICR product to get the variant ID
      const productResponse = await fetch(`/products/${icrProductHandle}.js`);
      if (!productResponse.ok) {
        throw new Error('Failed to fetch ICR adapter product');
      }
      
      const icrProduct = await productResponse.json();
      const variantId = icrProduct.variants?.[0]?.id;

      if (!variantId) {
        throw new Error('No variant found for ICR adapter product');
      }

      // CONSOLIDATION FIX: Check if ICR adapter already exists for this product
      const existingICRItem = await this.findExistingICRAdapter(productId, variantId);
      
      if (existingICRItem) {
        console.log('🔄 Cart drawer: ICR adapter already exists, updating quantity instead of adding new item');
        await this.updateICRAdapterQuantity(existingICRItem.key, existingICRItem.quantity + 1);
        
        // Still trigger the success flow
        this.showOptimisticSuccessMessage(addCartBtn, icrContainer);
        this._icrAddInProgress = false;
        return;
      }

      // Add to cart - exempt from performance manager batching via header
      
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-ICR-Request': 'true', // Flag for exemption
          'X-Line-Item-Context': lineItemKey // Track which line item triggered this
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: {
              '_icr_for_product': productId,
              '_icr_for_line_item': lineItemKey,
              '_icr_source': 'cart'
            }
          }]
        })
      });
      
      if (!cartResponse.ok) {
        const errorData = await cartResponse.json();
        throw new Error(errorData.message || 'Failed to add ICR adapter to cart');
      }

      // Phase 2: API succeeded - keep container hidden and show brief success message
      
      this.showOptimisticSuccessMessage(addCartBtn, icrContainer);

      this._icrAddInProgress = false;

      // Update cart with new contents but preserve optimistic UI
      
      // Immediate update to show the newly added ICR adapter
      setTimeout(async () => {
        try {
          // Step 1: Record which upsells should stay hidden to prevent flash
          const hiddenUpsells = new Set();
          this.querySelectorAll('.line-item-icr-upsell.icr-optimistic-hiding, .line-item-icr-upsell.icr-adapter-present').forEach(upsell => {
            const lineItemKey = upsell.dataset.lineItemKey;
            if (lineItemKey) {
              hiddenUpsells.add(lineItemKey);
              console.log(`🚫 PHASE 3: Marking line item ${lineItemKey} to stay hidden (prevent flash)`);
            }
          });
          
          // Step 2: Update cart content (including cart icon bubble)
          const sectionsResponse = await fetch(`${window.Shopify.routes.root}cart?section_id=cart-drawer`);
          const sectionsText = await sectionsResponse.text();
          
          // Also update cart icon bubble
          const bubbleResponse = await fetch(`${window.Shopify.routes.root}cart?section_id=cart-icon-bubble`);
          const bubbleText = await bubbleResponse.text();
          
          const parsedState = {
            sections: {
              'cart-drawer': sectionsText,
              'cart-icon-bubble': bubbleText
            }
          };
          
          // Step 3: Update cart contents to show new ICR adapter
          this.renderContents(parsedState);
          this.initializeICRUpsell();
          
          // Step 3.5: Reorder ICR adapters to appear below their related products
          this.reorderICRAdapters();
          
          // Step 4: Immediately hide the marked upsells to prevent flash
          hiddenUpsells.forEach(lineItemKey => {
            const upsellContainer = this.querySelector(`.line-item-icr-upsell[data-line-item-key="${lineItemKey}"]`);
            if (upsellContainer) {
              console.log(`⚡ PHASE 3: Immediately hiding upsell for ${lineItemKey} (prevent flash)`);
              
              // Apply instant hide class for flash prevention
              upsellContainer.classList.add('icr-flash-hide', 'icr-adapter-present');
              
              // Remove flash-hide class after DOM settles and re-enable transitions
              requestAnimationFrame(() => {
                if (upsellContainer) {
                  upsellContainer.classList.remove('icr-flash-hide');
                }
              });
            }
          });
          
          // Step 5: Run comprehensive check for any new adapters
          this.hideICRUpsellsForCompletedItems();

        } catch (updateError) {
          console.error('Failed to update cart drawer after ICR addition:', updateError);
        }
      }, 500); // Quick update to show new adapter

    } catch (error) {
      // API failed - restore upsell with error message
      this.restoreICRUpsellOnError(icrContainer, addCartBtn, error.message);
      
      this._icrAddInProgress = false;
    }
  }

  setICRButtonLoading(button, loading) {
    const spinner = button.querySelector('.loading-overlay__spinner');
    const buttonSpan = button.querySelector('span');
    
    if (loading) { 
      button.setAttribute('aria-disabled', true);
      button.classList.add('loading');
      
      // Show spinner
      if (spinner) {
        spinner.classList.remove('hidden');
      }
      
      // Update text
      if (buttonSpan) { 
        buttonSpan.textContent = 'Adding...';
      } else {
        button.textContent = 'Adding...';
      }
    } else {
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      
      // Hide spinner
      if (spinner) {
        spinner.classList.add('hidden');
      }
      
      // Don't reset text here - let showICRSuccessMessage handle it
    }
  }

  showICRSuccessMessage(addCartBtn, icrContainer) {
    if (!addCartBtn) return;
    
    // Remove loading class and spinner
    addCartBtn.classList.remove('loading');
    const spinner = addCartBtn.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.add('hidden');
    }
    
    // Update button text
    const buttonSpan = addCartBtn.querySelector('span');
    if (buttonSpan) {
      buttonSpan.textContent = 'Added';
    } else {
      addCartBtn.textContent = 'Added';
    }

    // Hide the entire ICR container after 3 seconds
    setTimeout(() => {
      if (icrContainer) {
        icrContainer.style.transition = 'opacity 0.3s ease-out, max-height 0.3s ease-out';
        icrContainer.style.opacity = '0';
        icrContainer.style.maxHeight = '0';
        icrContainer.style.overflow = 'hidden';
        
        // Remove from DOM after animation
        setTimeout(() => {
          if (icrContainer.parentNode) {
            icrContainer.parentNode.removeChild(icrContainer);
          }
        }, 300);
      }
    }, 3000);
  }

  handleICRError(errorMessage, addCartBtn) {
    // Show error in cart drawer
    const cartErrors = this.querySelector('#CartDrawer-CartErrors');
    if (cartErrors) {
      cartErrors.innerHTML = `
        <div class="icr-error-message" style="color: #dc2626; padding: 1rem; margin: 1rem 0; background: #fef2f2; border-radius: 0.5rem; border: 1px solid #dc2626;">
          ICR Upsell Error: ${errorMessage}
        </div>
      `;
    }
    
    // Reset button state
    if (addCartBtn) {
      this.setICRButtonLoading(addCartBtn, false);
      const buttonSpan = addCartBtn.querySelector('span');
      if (buttonSpan) {
        buttonSpan.textContent = 'Add to cart';
      } else {
        addCartBtn.textContent = 'Add to cart';
      }
    }
  }

  // Phase 2: Optimistic UI Methods

  hideICRUpsellOptimistically(icrContainer, addCartBtn) {
    // Instead of hiding, show loading state
    icrContainer.classList.add('icr-loading');
    addCartBtn.classList.add('loading');
    addCartBtn.setAttribute('aria-disabled', 'true');
    addCartBtn.disabled = true;
    
    // Store original state for potential restoration
    icrContainer._originalButtonText = addCartBtn.textContent;
    icrContainer._originalButtonClasses = addCartBtn.className;
    icrContainer._wasOptimisticallyHidden = true;

  }

  showOptimisticSuccessMessage(addCartBtn, icrContainer) {
    // Reset button loading state
    this.setICRButtonLoading(addCartBtn, false);
    
    // Now hide the upsell after successful addition
    icrContainer.classList.remove('icr-loading');
    icrContainer.classList.add('icr-optimistic-hiding');
    
    // Apply smooth hiding transition
    icrContainer.style.opacity = '0';
    icrContainer.style.maxHeight = '0px';
    icrContainer.style.overflow = 'hidden';
    icrContainer.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
    
    // Keep container hidden - don't restore
    // The success state is that the upsell is no longer needed

  }

  restoreICRUpsellOnError(icrContainer, addCartBtn, errorMessage) {
    // Only restore if it was optimistically hidden
    if (!icrContainer._wasOptimisticallyHidden) return;
    
    // Remove optimistic hiding class
    icrContainer.classList.remove('icr-optimistic-hiding');
    
    // Add restoring animation class
    icrContainer.classList.add('icr-restoring');
    
    // Restore original state with smooth animation
    setTimeout(() => {
      icrContainer.style.opacity = icrContainer._originalOpacity;
      icrContainer.style.maxHeight = icrContainer._originalMaxHeight || 'auto';
      icrContainer.style.overflow = icrContainer._originalOverflow;
      
      // Remove animation class after animation completes
      setTimeout(() => {
        icrContainer.classList.remove('icr-restoring');
      }, 300);
    }, 50);
    
    // Reset button loading state
    this.setICRButtonLoading(addCartBtn, false);
    
    // Show error message via existing error handler
    this.handleICRError(errorMessage, addCartBtn);
    
    console.error('🚀 PHASE 2: ICR upsell restored due to error:', errorMessage);
    
    // Clean up optimistic state
    delete icrContainer._originalOpacity;
    delete icrContainer._originalMaxHeight;
    delete icrContainer._originalOverflow;
    delete icrContainer._wasOptimisticallyHidden;
  }

  // ICR reordering disabled - section manager handles all ICR positioning
  reorderICRAdapters() {
    // DISABLED: Section manager now handles all ICR positioning and state management
    // This prevents conflicts between cart-drawer.js and section updates
    return;
  }
  
  // Helper method to get the target product ID for an ICR adapter
  getICRTargetProductId(icrElement) {
    // This could be enhanced to parse ICR adapter properties
    // For now, we'll use simple heuristics
    return null;
  }

  // CONSOLIDATION FIX: Find existing ICR adapter in cart for the same product
  async findExistingICRAdapter(productId, variantId) {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Look for existing ICR adapter with matching product ID or variant ID
      const existingItem = cart.items.find(item => {
        // Check if it's the same variant (ICR adapter product)
        if (item.variant_id === variantId) {
          // Check if it's for the same product (via properties)
          if (item.properties && item.properties._icr_for_product === productId) {
            return true;
          }
          // Also check if it has no specific product context (generic ICR adapter)
          if (!item.properties || (!item.properties._icr_for_product && !item.properties._icr_for_line_item)) {
            return true;
          }
        }
        return false;
      });

      return existingItem || null;
    } catch (error) {
      console.warn('Cart drawer: Failed to check for existing ICR adapter:', error);
      return null;
    }
  }

  // CONSOLIDATION FIX: Update quantity of existing ICR adapter
  async updateICRAdapterQuantity(itemKey, newQuantity) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: newQuantity
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update ICR adapter quantity');
      }

      console.log(`🔄 Cart drawer: Successfully updated ICR adapter quantity to ${newQuantity}`);
      
      // Trigger cart update
      setTimeout(async () => {
        try {
          const sectionsResponse = await fetch(`${window.Shopify.routes.root}cart?section_id=cart-drawer`);
          const sectionsText = await sectionsResponse.text();
          
          const bubbleResponse = await fetch(`${window.Shopify.routes.root}cart?section_id=cart-icon-bubble`);
          const bubbleText = await bubbleResponse.text();
          
          const parsedState = {
            sections: {
              'cart-drawer': sectionsText,
              'cart-icon-bubble': bubbleText
            }
          };
          
          this.renderContents(parsedState);
          this.initializeICRUpsell();
          this.reorderICRAdapters();
          this.hideICRUpsellsForCompletedItems();
          
        } catch (updateError) {
          console.error('Failed to update cart drawer after ICR quantity change:', updateError);
        }
      }, 300);
      
    } catch (error) {
      console.error('Cart drawer: Failed to update ICR adapter quantity:', error);
      throw error;
    }
  }

  // Phase 3: Hide ICR upsells for line items that now have ICR adapters in cart
  hideICRUpsellsForCompletedItems() {
    try {
      // Get current cart state to check for ICR adapters
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {

          // Find all ICR adapters in the cart (both cart drawer and PDP added)
          const icrAdapters = cart.items.filter(item => {
            // Check if it's an ICR adapter by variant ID and has ICR properties
            const isICRAdapter = item.variant_id === 46698355491052 || // Hardcoded ICR adapter variant
                               (item.title && item.title.toLowerCase().includes('icr adapter'));
            
            const hasICRProperties = item.properties && (
              item.properties._icr_for_line_item ||  // Cart drawer added
              item.properties._icr_for_product ||    // PDP added  
              item.properties._icr_source            // Either source
            );
            
            return isICRAdapter && hasICRProperties;
          });

          // Hide upsells for line items that have adapters
          icrAdapters.forEach(adapter => {
            // Handle both cart drawer added (has _icr_for_line_item) and PDP added (has _icr_for_product)
            if (adapter.properties._icr_for_line_item) {
              // Cart drawer added adapter - hide specific line item upsell
              const lineItemKey = adapter.properties._icr_for_line_item;
              
              // Try multiple selectors to find the upsell container
              let upsellContainer = this.querySelector(`[data-line-item-key="${lineItemKey}"] .line-item-icr-upsell`);
              
              if (!upsellContainer) {
                // Alternative selector - look for the exact line item key
                upsellContainer = this.querySelector(`.line-item-icr-upsell[data-line-item-key="${lineItemKey}"]`);

              }
              
              if (!upsellContainer) {
                // Debug: Show all available ICR upsell containers
                const allUpsells = this.querySelectorAll('.line-item-icr-upsell');
                allUpsells.forEach((container, index) => {
                  console.log(`  ${index + 1}. Line item key: "${container.dataset.lineItemKey}"`);
                });
              }
              
              if (upsellContainer) {

                // Remove optimistic hiding class and apply permanent hiding
                upsellContainer.classList.remove('icr-optimistic-hiding');
                upsellContainer.classList.add('icr-adapter-present');
                
                // Ensure it stays hidden with the adapter-present styling
                upsellContainer.style.opacity = '0';
                upsellContainer.style.maxHeight = '0px';
                upsellContainer.style.overflow = 'hidden';
                upsellContainer.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
                
                // Clean up any optimistic state tracking
                delete upsellContainer._originalOpacity;
                delete upsellContainer._originalMaxHeight;
                delete upsellContainer._originalOverflow;
                delete upsellContainer._wasOptimisticallyHidden;
              } else {
                console.warn(`Could not find ICR upsell container for line item ${lineItemKey}`);
              }
              
            } else if (adapter.properties._icr_for_product) {
              // PDP added adapter - hide all upsells for this product
              const productId = adapter.properties._icr_for_product;
              
              // Find all ICR upsells for this product
              const productUpsells = this.querySelectorAll(`.line-item-icr-upsell[data-product-id="${productId}"]`);

              productUpsells.forEach(upsellContainer => {

                // Remove optimistic hiding class and apply permanent hiding
                upsellContainer.classList.remove('icr-optimistic-hiding');
                upsellContainer.classList.add('icr-adapter-present');
                
                // Ensure it stays hidden with the adapter-present styling
                upsellContainer.style.opacity = '0';
                upsellContainer.style.maxHeight = '0px';
                upsellContainer.style.overflow = 'hidden';
                upsellContainer.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
                
                // Clean up any optimistic state tracking
                delete upsellContainer._originalOpacity;
                delete upsellContainer._originalMaxHeight;
                delete upsellContainer._originalOverflow;
                delete upsellContainer._wasOptimisticallyHidden;
              });
            }
          });
        })
        .catch(error => {
          console.warn('🚀 PHASE 3: Failed to check cart for ICR adapters:', error);
        });
    } catch (error) {
      console.warn('🚀 PHASE 3: Error in hideICRUpsellsForCompletedItems:', error);
    }
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

} 

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() { 
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // Override to ensure cart drawer updates publish correct source
  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const sectionsToRequest = this.getSectionsToRender().map((section) => section.section).join(',');
    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionsToRequest,
      sections_url: window.location.pathname,
    });

    // Add X-Cart-Drawer header to bypass performance manager batching for drawer updates
    fetch(`${routes.cart_change_url}`, { 
      ...fetchConfig(), 
      body,
      headers: {
        ...fetchConfig().headers,
        'X-Cart-Drawer': 'true' // Mark as cart drawer request
      }
    })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartFooter = document.getElementById('main-cart-footer');
        const cartDrawerWrapper = document.querySelector('cart-drawer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        // Update drawer sections
        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          
          if (elementToReplace && parsedState.sections[section.section]) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        // Re-initialize ICR upsell after updates
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.initializeICRUpsell === 'function') {
          cartDrawer.initializeICRUpsell();
        }

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`));
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }
        
        // Publish cart drawer update event
        publish('cart-update', { 
          source: 'cart-drawer',
          item_count: parsedState.item_count,
          total_price: parsedState.total_price 
        });
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        document.getElementById(`CartDrawer-LineItemError-${line}`)
          .querySelector('.cart-item__error-text')
          .innerHTML = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }
  
  // CRITICAL FIX: Override onCartUpdate to fetch cart-drawer content instead of main-cart-items
  onCartUpdate() {
    // Fetch cart-drawer section instead of main-cart-items
    fetch(`${window.Shopify.routes.root}cart?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceContent = html.querySelector('cart-drawer-items');
        
        if (sourceContent) {
          this.innerHTML = sourceContent.innerHTML;
          // Re-initialize ICR upsell after content update
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.initializeICRUpsell === 'function') {
            cartDrawer.initializeICRUpsell();
          }
        }
        
        // Publish cart update event so main cart page can update too
        publish('cart-update', { source: 'cart-drawer' });
      })
      .catch((e) => {
        // Silently handle errors
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
